/**
 * Client fog-of-war visual (feat/fog-of-war, Phase 4; feathered shroud pass
 * 2026-07-02).
 *
 * Renders the fog states over the terrain as a KTS-style SOFT shroud with two
 * visually distinct tiers plus feathered boundaries:
 *   - unseen           = deep dark COOL tone; the terrain silhouette barely
 *                        ghosts through (never pure black — reads "unknown",
 *                        not "void").
 *   - explored (memory)= a dimmed, cooled version of the world — you can still
 *                        read remembered terrain, clearly darker + cooler than
 *                        live vision.
 *   - currently visible= fully clear (shroud alpha 0), so the live field stays
 *                        as readable as the underlying render, day AND night.
 *
 * The *authoritative* hiding of enemies is done server-side via StateView — this
 * module is purely the terrain shroud so the player can read where they've
 * scouted. It reuses the SAME shared vision math (shared/vision.ts
 * `computeVisibleCells`) that the server uses to gate entities, fed the SAME
 * heightmap via `sampleHeight`, so the lit region the player sees always matches
 * the region where enemies can actually appear (no enemy popping out of "fogged"
 * ground).
 *
 * FEATHERING — how the hard grid staircase is removed:
 *   The old shroud snapped each terrain vertex to ONE fog cell's discrete alpha,
 *   so cell boundaries showed as a stair-step at typical zoom. Now the fog grid
 *   is uploaded to a small `DataTexture` (RG: visible / explored as smoothed
 *   floats) with LINEAR filtering, and a `ShaderMaterial` on the cloned terrain
 *   geometry samples it by WORLD-XZ per fragment. Two things soften the edge:
 *     (1) a separable box blur of the binary grids into float fields when the
 *         texture is rebuilt (`FEATHER_CELLS`), and
 *     (2) the texture's own bilinear filtering between texels.
 *   The result feathers the visible↔explored↔unseen boundaries over a few world
 *   units instead of stepping per grid cell — at any zoom, per pixel.
 *
 * Cloning the terrain geometry (play mesh + skirt) means the shroud hugs every
 * cliff and ramp exactly, and the same continuous vision field blankets the
 * beyond-edge skirt (issue #4).
 */
import * as THREE from "three";
import {
  computeVisibleCells,
  type FogGrid,
  type VisionSource,
} from "@tribez/shared";
import { fogMaskUniforms } from "./world/fog-mask";

// The fog grid covers the playable map PLUS a pad ring so the terrain skirt
// (world/terrain.ts) is shrouded by the SAME continuous vision field — the dark
// unseen shroud then blankets the extended terrain seamlessly, with no line at
// the old map boundary (issue #4). Only cells near a vision source are tested
// each update, so the larger grid stays cheap. cellSize 2 captures ramp detail.
const MAP_UNITS = 256;
const FOG_PAD = 128;                 // world units of skirt covered beyond each edge
const FOG_CELL = 2;
const FOG_ORIGIN = -FOG_PAD;         // world coord of cell (0,0)
const FOG_COLS = (MAP_UNITS + 2 * FOG_PAD) / FOG_CELL; // 256
const FOG_ROWS = FOG_COLS;
const FOG_EXTENT = FOG_COLS * FOG_CELL; // world units spanned by the grid/texture (512)

// --- Tier darkness (tunable) --------------------------------------------------
// Shroud alpha of the dark overlay colour, per tier. Higher = darker/more hidden.
//   UNSEEN   : deep dark — 0.84 leaves ~16% of the terrain bleeding through so the
//              silhouette barely ghosts (matches the KTS reference read), not a
//              void-black wall.
//   EXPLORED : remembered terrain, clearly dimmed + cooled but still legible.
//   VISIBLE  : 0 — the live field is untouched, so night readability of what you
//              can actually see is never crushed (feedback_night_atmosphere_readable).
const ALPHA_UNSEEN = 0.84;
const ALPHA_EXPLORED = 0.44;
// A very dark COOL blue-grey — NOT pure black. Over the terrain this reads as the
// cool "unknown" of the KTS shroud; at EXPLORED alpha it cools + dims the
// remembered ground. Tunable.
const FOG_COLOUR = 0x0a0f19;

// --- Feathering (tunable) -----------------------------------------------------
// Radius, in fog cells (FOG_CELL = 2 world units each), of the separable box blur
// applied to the binary visible/explored grids before upload. 1 → a ~2-cell ramp
// (~4 world units); combined with the texture's bilinear filtering the boundaries
// blur smoothly with no grid staircase. 0 still feathers ~1 texel via the linear
// filter alone. Higher = softer/wider halo.
const FEATHER_CELLS = 1;
// Smoothstep shaping of the blurred fields in the shader: below LOW reads fully as
// the outer tier, above HIGH fully as the inner tier, with a smooth ramp between.
// Keeps the feather band controlled and the tier interiors clean (no faint haze).
const FEATHER_LOW = 0.15;
const FEATHER_HIGH = 0.85;

// Recompute the fog VISION grid at ~6Hz; the expensive per-source cell sweep +
// spatial box blur run at this cadence. The texture itself is re-uploaded every
// frame (cheap) so the temporal ease below stays buttery regardless of this rate.
const FOG_UPDATE_MS = 160;

// --- Temporal reveal (tunable) ------------------------------------------------
// The shroud used to SNAP between tiers at the 6Hz recompute — scouting popped the
// fog open in visible steps. Now each cell's visible/explored value eases toward
// its freshly-computed target every frame (exponential smoothing, per-cell), so
// the fog wipes open as you scout and creeps back as you leave. Time constants in
// ms: reveal is snappier than re-shroud so gaining vision feels responsive while
// losing it feels like fog rolling back in. Explored-ness only ever rises (it's
// map memory) and eases in on first discovery at the REVEAL rate.
const REVEAL_TAU_MS = 200;   // fog lifting  (cell gains vision / first explored)
const RESHROUD_TAU_MS = 480; // fog returning (cell drops out of live vision)

export interface FogApi {
  /**
   * Advance the fog. `sources` = the local team's living vision sources (own +
   * ally cubes, with their sight radius). `nowMs` throttles internally.
   */
  tick(sources: VisionSource[], nowMs: number): void;
  dispose(): void;
  readonly mesh: THREE.Mesh;
  /** Current fog grids — consumed by the minimap to shade scouted area. The grid
   *  now extends past the map (origin < 0); the minimap clips to [0, MAP_UNITS]. */
  fogState(): { visible: Uint8Array; explored: Uint8Array; cols: number; rows: number; cellSize: number; originX: number; originZ: number };
  /** Smoothed fog state at a world point, for gating overhead UI (e.g. resource
   *  HP bars show only where you have LIVE vision, not merely explored memory).
   *  Returns 0..1 tiers matching what the shroud draws. Outside the grid → hidden. */
  sampleFog(x: number, z: number): { visible: number; explored: number };
}

const VERT_SHADER = /* glsl */ `
  varying vec2 vWorldXZ;
  void main() {
    // World XZ of this vertex (the mesh has no rotation/scale, only a tiny y lift,
    // so XZ passes straight through modelMatrix — but use it for correctness).
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldXZ = wp.xz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG_SHADER = /* glsl */ `
  precision mediump float;
  uniform sampler2D uFogTex;   // R = visible (0..1), G = explored (0..1), smoothed
  uniform vec3  uColor;        // dark cool shroud tint
  uniform vec2  uOrigin;       // world coord of the grid's (0,0) corner
  uniform float uExtent;       // world units spanned by the grid
  uniform float uAlphaUnseen;
  uniform float uAlphaExplored;
  uniform float uFeatherLow;
  uniform float uFeatherHigh;
  varying vec2 vWorldXZ;

  void main() {
    // World → grid UV. ClampToEdge wrapping means the far skirt (uv outside 0..1)
    // reads the edge cell, which is always unseen → the beyond-map ground stays
    // fully shrouded with no bright band (issue #4).
    vec2 uv = (vWorldXZ - uOrigin) / uExtent;
    vec2 fog = texture2D(uFogTex, uv).rg;
    float vis = smoothstep(uFeatherLow, uFeatherHigh, fog.r);
    float exp = smoothstep(uFeatherLow, uFeatherHigh, fog.g);
    // exploredness lifts the shroud from UNSEEN toward EXPLORED; visibility then
    // clears it the rest of the way. Both inputs are smoothed → feathered edges.
    float a = mix(uAlphaUnseen, uAlphaExplored, exp);
    a = mix(a, 0.0, vis);
    gl_FragColor = vec4(uColor, a);
  }
`;

export function createFog(opts: {
  scene: THREE.Scene;
  terrainGeometry: THREE.BufferGeometry;
  /** Extra surfaces to shroud alongside the play mesh — e.g. the terrain skirt
   *  (world/terrain.ts). Their vertices sample the SAME world-XZ fog field, with
   *  texture ClampToEdge so beyond-edge ground inherits the edge (unseen) cell →
   *  the shroud is continuous across the boundary (no bright-skirt line). */
  extraGeometries?: THREE.BufferGeometry[];
  sampleHeight: (x: number, z: number) => number;
}): FogApi {
  const grid: FogGrid = {
    cols: FOG_COLS,
    rows: FOG_ROWS,
    cellSize: FOG_CELL,
    originX: FOG_ORIGIN,
    originZ: FOG_ORIGIN,
  };
  const visible = new Uint8Array(FOG_COLS * FOG_ROWS);
  const explored = new Uint8Array(FOG_COLS * FOG_ROWS);

  // Overlay geometry = the terrain surface + any extra surfaces (skirt), merged
  // into one position/index buffer so a single shroud covers the whole ground.
  const inputs = [opts.terrainGeometry, ...(opts.extraGeometries ?? [])];
  let vertCount = 0;
  let idxCount = 0;
  for (const g of inputs) {
    vertCount += (g.getAttribute("position") as THREE.BufferAttribute).count;
    const gi = g.getIndex();
    idxCount += gi ? gi.count : 0;
  }
  const posArr = new Float32Array(vertCount * 3);
  const idxArr = new Uint32Array(idxCount);
  let vOff = 0;
  let iOff = 0;
  for (const g of inputs) {
    const gp = g.getAttribute("position") as THREE.BufferAttribute;
    posArr.set((gp.array as Float32Array).subarray(0, gp.count * 3), vOff * 3);
    const gi = g.getIndex();
    if (gi) {
      for (let k = 0; k < gi.count; k++) idxArr[iOff + k] = gi.getX(k) + vOff;
      iOff += gi.count;
    }
    vOff += gp.count;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(posArr, 3));
  geom.setIndex(new THREE.BufferAttribute(idxArr, 1));

  // Fog field texture: RG bytes, LINEAR filtered so the GPU bilinearly interpolates
  // between cells (half the feathering; the box blur below supplies the rest). One
  // texel per fog cell; sampled by world-XZ in the fragment shader.
  const texData = new Uint8Array(FOG_COLS * FOG_ROWS * 2);
  const fogTex = new THREE.DataTexture(texData, FOG_COLS, FOG_ROWS, THREE.RGFormat, THREE.UnsignedByteType);
  fogTex.minFilter = THREE.LinearFilter;
  fogTex.magFilter = THREE.LinearFilter;
  fogTex.wrapS = THREE.ClampToEdgeWrapping;
  fogTex.wrapT = THREE.ClampToEdgeWrapping;
  fogTex.generateMipmaps = false;
  fogTex.needsUpdate = true;

  // Publish this texture as the SHARED fog mask so doodad + resource-node toon
  // materials dissolve where the ground is never-explored (they read the G/explored
  // channel by world-XZ — see world/fog-mask.ts + toon.ts `fogMask`). Kept disabled
  // until the first vision sweep populates `explored`, so nothing blanks on load.
  fogMaskUniforms.uFogMask.value = fogTex;
  fogMaskUniforms.uFogMaskOrigin.value.set(FOG_ORIGIN, FOG_ORIGIN);
  fogMaskUniforms.uFogMaskExtent.value = FOG_EXTENT;
  fogMaskUniforms.uFogMaskEnabled.value = 0;

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uFogTex: { value: fogTex },
      uColor: { value: new THREE.Color(FOG_COLOUR) },
      uOrigin: { value: new THREE.Vector2(FOG_ORIGIN, FOG_ORIGIN) },
      uExtent: { value: FOG_EXTENT },
      uAlphaUnseen: { value: ALPHA_UNSEEN },
      uAlphaExplored: { value: ALPHA_EXPLORED },
      uFeatherLow: { value: FEATHER_LOW },
      uFeatherHigh: { value: FEATHER_HIGH },
    },
    vertexShader: VERT_SHADER,
    fragmentShader: FRAG_SHADER,
    transparent: true,
    depthWrite: false,
    fog: false,
    // DoubleSide is REQUIRED: the merged-in terrain-skirt collar (world/terrain.ts)
    // has mixed triangle winding from its radial extrusion — its own mesh renders
    // DoubleSide for the same reason. With the default FrontSide the shroud
    // backface-culls those skirt triangles and never paints over the extended
    // terrain, leaving a bright un-shrouded band past the map edge (proven by live
    // pixel measurement, 2026-06-18).
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.position.y = 0.08; // tiny lift over the terrain to avoid z-fighting
  mesh.renderOrder = 3; // after terrain + doodads
  mesh.frustumCulled = false;
  mesh.raycast = () => {}; // never intercept picks
  opts.scene.add(mesh);

  // Float fields (reused each tick — no per-frame alloc):
  //   visF/expF  — this recompute's binary grids seeded to 0/1, then box-blurred
  //                IN PLACE into the spatially-feathered TARGET the ease chases.
  //   visSmooth/expSmooth — the per-frame temporally-eased values actually packed
  //                into the texture. `sampleFog` reads these so gating matches pixels.
  //   tmpF       — separable-blur scratch.
  const visF = new Float32Array(FOG_COLS * FOG_ROWS);
  const expF = new Float32Array(FOG_COLS * FOG_ROWS);
  const tmpF = new Float32Array(FOG_COLS * FOG_ROWS);
  const visSmooth = new Float32Array(FOG_COLS * FOG_ROWS);
  const expSmooth = new Float32Array(FOG_COLS * FOG_ROWS);

  // Separable box blur of `src` (radius r cells) into `dst`, via `tmp` scratch.
  // Feathers the binary grid into a smooth 0..1 ramp before the texture upload.
  function boxBlur(src: Float32Array, dst: Float32Array, tmp: Float32Array, r: number): void {
    if (r <= 0) {
      dst.set(src);
      return;
    }
    const w = FOG_COLS, h = FOG_ROWS;
    const norm = 1 / (2 * r + 1);
    // horizontal pass: src → tmp
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        let sum = 0;
        for (let k = -r; k <= r; k++) {
          const xx = Math.min(w - 1, Math.max(0, x + k));
          sum += src[row + xx]!;
        }
        tmp[row + x] = sum * norm;
      }
    }
    // vertical pass: tmp → dst
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        let sum = 0;
        for (let k = -r; k <= r; k++) {
          const yy = Math.min(h - 1, Math.max(0, y + k));
          sum += tmp[yy * w + x]!;
        }
        dst[y * w + x] = sum * norm;
      }
    }
  }

  let lastRecompute = -1e9;
  let lastFrameMs = -1;
  let firstTick = true;

  function tick(sources: VisionSource[], nowMs: number): void {
    // Per-frame dt for the temporal ease (clamped so a tab-refocus stall doesn't
    // teleport the fog open in one giant step).
    if (lastFrameMs < 0) lastFrameMs = nowMs;
    const dt = Math.min(100, Math.max(0, nowMs - lastFrameMs));
    lastFrameMs = nowMs;

    const n = explored.length;

    // ~6Hz: recompute the vision sweep and spatially feather it into the TARGET
    // fields (visF/expF) that the per-frame ease chases. Off the hot path.
    if (nowMs - lastRecompute >= FOG_UPDATE_MS) {
      lastRecompute = nowMs;
      computeVisibleCells(sources, opts.sampleHeight, grid, visible);
      for (let i = 0; i < n; i++) {
        if (visible[i]) explored[i] = 1;
        visF[i] = visible[i] ? 1 : 0;
        expF[i] = explored[i] ? 1 : 0;
      }
      boxBlur(visF, visF, tmpF, FEATHER_CELLS);
      boxBlur(expF, expF, tmpF, FEATHER_CELLS);

      // First sweep: SNAP the eased fields to the target so the spawn area starts
      // solid (no one-frame flash where already-explored ground dissolves and
      // re-forms), then arm the doodad mask.
      if (firstTick) {
        firstTick = false;
        visSmooth.set(visF);
        expSmooth.set(expF);
        fogMaskUniforms.uFogMaskEnabled.value = 1;
      }
    }

    // Every frame: ease toward the target and re-upload. Reveal (value rising)
    // uses the snappy tau; re-shroud (visibility falling) uses the slower one.
    // Explored is monotonic map-memory → only ever rises, at the reveal rate.
    const kRise = 1 - Math.exp(-dt / REVEAL_TAU_MS);
    const kFall = 1 - Math.exp(-dt / RESHROUD_TAU_MS);
    for (let i = 0; i < n; i++) {
      const vt = visF[i]!;
      const vs = visSmooth[i]!;
      visSmooth[i] = vs + (vt - vs) * (vt >= vs ? kRise : kFall);
      const et = expF[i]!;
      const es = expSmooth[i]!;
      if (et > es) expSmooth[i] = es + (et - es) * kRise;
      texData[i * 2] = Math.round(visSmooth[i]! * 255);
      texData[i * 2 + 1] = Math.round(expSmooth[i]! * 255);
    }
    fogTex.needsUpdate = true;
  }

  function sampleFog(x: number, z: number): { visible: number; explored: number } {
    const cx = Math.floor((x - FOG_ORIGIN) / FOG_CELL);
    const cz = Math.floor((z - FOG_ORIGIN) / FOG_CELL);
    if (cx < 0 || cz < 0 || cx >= FOG_COLS || cz >= FOG_ROWS) {
      return { visible: 0, explored: 0 };
    }
    const idx = cz * FOG_COLS + cx;
    return { visible: visSmooth[idx]!, explored: expSmooth[idx]! };
  }

  function dispose(): void {
    // Detach the shared mask so doodads render un-masked after teardown (the
    // texture is about to be freed); swap back to the 1×1 placeholder.
    fogMaskUniforms.uFogMaskEnabled.value = 0;
    opts.scene.remove(mesh);
    geom.dispose();
    mat.dispose();
    fogTex.dispose();
  }

  function fogState() {
    return { visible, explored, cols: FOG_COLS, rows: FOG_ROWS, cellSize: FOG_CELL, originX: FOG_ORIGIN, originZ: FOG_ORIGIN };
  }

  return { tick, dispose, mesh, fogState, sampleFog };
}
